import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

const LOCALE_MAP: Record<string, string> = {
  es: 'es_ES',
  en: 'en_US',
};

const LANGUAGE_NAME_MAP: Record<string, string> = {
  es: 'Spanish',
  en: 'English',
};

const DEFAULT_TITLES: Record<string, string> = {
  es: 'Psymetrika - Evaluación Psicométrica con IA',
  en: 'Psymetrika - AI-powered Psychometric Assessment',
};

const DEFAULT_DESCRIPTIONS: Record<string, string> = {
  es: 'Plataforma profesional de evaluación psicométrica potenciada con inteligencia artificial. Tests validados, análisis clínicos automáticos y reportes detallados para profesionales de la salud mental.',
  en: 'Professional psychometric assessment platform powered by artificial intelligence. Validated tests, automatic clinical analyses and detailed reports for mental health professionals.',
};

const DEFAULT_KEYWORDS: Record<string, string> = {
  es: 'psicometría, evaluación psicológica, tests psicológicos, inteligencia artificial, salud mental, análisis clínico, reportes psicológicos, profesionales de la salud',
  en: 'psychometrics, psychological assessment, psychological tests, artificial intelligence, mental health, clinical analysis, psychological reports, mental health professionals',
};

export default function SEO({
  title,
  description,
  keywords,
  ogType = 'website',
  ogImage,
  canonical,
  noindex = false,
}: SEOProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'es';

  const siteName = 'Psymetrika';
  const defaultImage = 'https://psymetrika.com/og-image.png';
  const siteUrl = 'https://psymetrika.com';

  const defaultTitle = DEFAULT_TITLES[lang] ?? DEFAULT_TITLES.es;
  const defaultDescription = DEFAULT_DESCRIPTIONS[lang] ?? DEFAULT_DESCRIPTIONS.es;
  const defaultKeywords = DEFAULT_KEYWORDS[lang] ?? DEFAULT_KEYWORDS.es;
  const ogLocale = LOCALE_MAP[lang] ?? 'es_ES';
  const langName = LANGUAGE_NAME_MAP[lang] ?? 'Spanish';

  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageImage = ogImage || defaultImage;
  const pageUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />

      {/* hreflang — same URL serves both languages */}
      <link rel="alternate" hrefLang="es" href={pageUrl} />
      <link rel="alternate" hrefLang="en" href={pageUrl} />
      <link rel="alternate" hrefLang="x-default" href={pageUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1408" />
      <meta property="og:image:height" content="736" />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Psymetrika" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content={langName} />

      {/* Theme Color */}
      <meta name="theme-color" content="#8b5cf6" />
      <meta name="msapplication-TileColor" content="#8b5cf6" />
    </Helmet>
  );
}
